<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue'

/**
 * PDialog — dialog de CONFIRMATION (vs PModal qui est un container générique
 * pour formulaires longs). Expose une API confirm/cancel sans avoir à câbler
 * les boutons manuellement.
 *
 * Remplace TOUS les `window.confirm()` / `window.alert()` / `window.prompt()`
 * du code (Revelio règle §9.6). Le pattern minimal :
 *
 *   <PDialog
 *     v-model:open="confirmDeleteOpen"
 *     title="Supprimer cette clé ?"
 *     description="La révocation est immédiate et irréversible."
 *     variant="destructive"
 *     confirm-label="Supprimer"
 *     :loading="deleting"
 *     @confirm="onConfirmDelete"
 *   />
 *
 * Le parent contrôle le statut via v-model et écoute @confirm. Le close
 * (cancel + ESC + backdrop) émet @cancel + ferme automatiquement.
 *
 * Pour un formulaire complexe (create user, etc.), utiliser PModal.
 */
interface Props {
    open: boolean
    title: string
    description?: string
    confirmLabel?: string
    cancelLabel?: string
    /** destructive = bouton confirm en danger (brick). default = primary ink. */
    variant?: 'default' | 'destructive'
    /** Si true, désactive cancel/ESC/backdrop pendant l'action async. */
    loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    confirmLabel: '',
    cancelLabel: '',
    variant: 'default',
    loading: false,
})

const emit = defineEmits<{
    (e: 'update:open', value: boolean): void
    (e: 'confirm'): void
    (e: 'cancel'): void
}>()

function cancel() {
    if (props.loading) return
    emit('cancel')
    emit('update:open', false)
}

function confirm() {
    emit('confirm')
    // Le parent ferme via v-model quand l'action async est terminée.
}

function onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && props.open && !props.loading) {
        cancel()
    }
}

watch(
    () => props.open,
    (isOpen) => {
        if (typeof document === 'undefined') return
        document.body.style.overflow = isOpen ? 'hidden' : ''
    },
)

onMounted(() => {
    document.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
    document.removeEventListener('keydown', onKeydown)
    if (typeof document !== 'undefined') {
        document.body.style.overflow = ''
    }
})
</script>

<template>
    <Teleport to="body">
        <Transition name="fade">
            <div
                v-if="open"
                class="p-dialog-backdrop"
                role="presentation"
                @click.self="cancel"
            >
                <Transition name="dialog" appear>
                    <div
                        v-if="open"
                        class="p-dialog"
                        role="alertdialog"
                        aria-modal="true"
                        aria-labelledby="p-dialog-title"
                        :aria-describedby="description ? 'p-dialog-desc' : undefined"
                    >
                        <div class="p-dialog__body">
                            <h3 id="p-dialog-title" class="p-dialog__title">{{ title }}</h3>
                            <p v-if="description" id="p-dialog-desc" class="p-dialog__desc">
                                {{ description }}
                            </p>
                            <slot />
                        </div>
                        <div class="p-dialog__actions">
                            <button
                                type="button"
                                class="p-dialog__btn p-dialog__btn--cancel"
                                :disabled="loading"
                                @click="cancel"
                            >
                                {{ cancelLabel || $t('common.cancel') }}
                            </button>
                            <button
                                type="button"
                                :class="[
                                    'p-dialog__btn',
                                    variant === 'destructive'
                                        ? 'p-dialog__btn--destructive'
                                        : 'p-dialog__btn--confirm',
                                ]"
                                :disabled="loading"
                                :aria-busy="loading ? 'true' : 'false'"
                                @click="confirm"
                            >
                                <span v-if="loading" class="p-dialog__spinner p-animate-spin" aria-hidden="true" />
                                {{ confirmLabel || $t('common.confirm') }}
                            </button>
                        </div>
                    </div>
                </Transition>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
.p-dialog-backdrop {
    position: fixed;
    inset: 0;
    background: var(--p-color-overlay);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--p-space-4);
    z-index: 1000;
}

.p-dialog {
    background: var(--p-color-surface);
    border: 1px solid var(--p-color-line);
    border-radius: var(--p-radius-sm);
    box-shadow: var(--p-shadow-modal);
    width: 100%;
    max-width: 400px;
}

.p-dialog__body {
    padding: 24px 24px 16px;
}

.p-dialog__title {
    margin: 0;
    font-size: var(--p-text-sm);
    font-weight: var(--p-font-weight-medium);
    color: var(--p-color-ink);
}

.p-dialog__desc {
    margin: 8px 0 0;
    font-size: var(--p-text-xs);
    color: var(--p-color-ink-4);
    line-height: var(--p-line-relaxed);
}

.p-dialog__actions {
    display: flex;
    gap: var(--p-space-3);
    padding: 8px 24px 20px;
}

.p-dialog__btn {
    flex: 1;
    padding: 8px 14px;
    font-family: var(--p-font-sans);
    font-size: var(--p-text-sm);
    font-weight: var(--p-font-weight-medium);
    border: 1px solid transparent;
    border-radius: var(--p-radius-sm);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--p-space-2);
    transition:
        background var(--p-motion-fast) var(--p-ease-out),
        border-color var(--p-motion-fast) var(--p-ease-out),
        color var(--p-motion-fast) var(--p-ease-out);
}
.p-dialog__btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.p-dialog__btn--cancel {
    background: var(--p-color-surface);
    color: var(--p-color-ink-4);
    border-color: var(--p-color-line);
}
.p-dialog__btn--cancel:hover:not(:disabled) {
    color: var(--p-color-ink);
    border-color: var(--p-color-ink-6);
}

.p-dialog__btn--confirm {
    background: var(--p-color-ink);
    color: var(--p-color-surface);
}
.p-dialog__btn--confirm:hover:not(:disabled) {
    background: var(--p-color-ink-2);
}

.p-dialog__btn--destructive {
    background: var(--p-color-brick);
    color: var(--p-color-surface);
}
.p-dialog__btn--destructive:hover:not(:disabled) {
    background: color-mix(in srgb, var(--p-color-brick) 90%, black);
}

.p-dialog__spinner {
    width: 0.9em;
    height: 0.9em;
    border: 1.5px solid currentColor;
    border-top-color: transparent;
    border-radius: var(--p-radius-full);
    display: inline-block;
}
</style>
